import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  joinStatsApi,
  type WeekDashboard,
  type WeekDayRow,
  type WeekHourRow,
  type WeekStatus,
} from '../../api/joinstats.js';
import { Spinner, ErrorBox } from '../../components/ui.js';
import { JoinHealthBanner } from './JoinHealthBanner.js';

/**
 * 신규가입 전환 대시보드 (요일별). 원본 marketing/join_member_week/dashboard.php.
 *
 * 날짜별 화면(JoinDashboardPage)과 보는 각도가 다르다. 이쪽 기준선은 **요일 ×
 * 시간** 이라, "이번 월요일 14시" 를 "다른 월요일들의 14시" 와 견준다. 그래서
 * 표에 평균만이 아니라 중앙값·표준편차·Z 가 함께 나온다 - 몇 명 줄었는지보다
 * "평소 흔들리는 폭을 넘었는가" 가 판단의 근거다.
 *
 * 구성도 원본 그대로다: 요일 버튼 → 지표 카드 → 기준 시점 진단 → 일자별 상세
 * (행에 마우스를 올리면 오른쪽에 그 날의 시간대별 내역) → 우선 확인 시간 →
 * 시간대 차트. 색만 이 앱의 어두운 테마 토큰으로 옮겼다.
 */

const DOW_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

type ChartMode = 'conversion' | 'inflow' | 'average';

const fmtNum = (v: number | null | undefined) => (Number(v) || 0).toLocaleString('ko-KR');

function fmtRate(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return `${Number(v).toFixed(1).replace(/\.0$/, '')}%`;
}

function fmtAvg(v: number | null | undefined): string {
  return (Number(v) || 0).toFixed(1).replace(/\.0$/, '');
}

/** 원본 formatZ */
function fmtZ(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  const value = Number(v);
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}`;
}

export function JoinWeekdayPage() {
  const [params, setParams] = useSearchParams();

  const week = Number(params.get('week') ?? new Date().getDay());
  const zThreshold = params.get('zThreshold') ?? '2';
  const from = params.get('from') ?? undefined;
  const to = params.get('to') ?? undefined;

  const dash = useQuery({
    queryKey: ['join-weekday', from, to, zThreshold, week],
    queryFn: () => joinStatsApi.weekday({ from, to, zThreshold, week }),
  });

  // 조회 조건은 원본처럼 [조회] 를 눌러야 반영된다. 요일 버튼만 바로 조회한다
  // (원본 searchWeek 도 그렇다 - 요일은 조건이 아니라 화면 그 자체다).
  const [draft, setDraft] = useState({ from: from ?? '', to: to ?? '', zThreshold });

  function apply(next: { week?: number }) {
    const q: Record<string, string> = {
      zThreshold: draft.zThreshold,
      week: String(next.week ?? week),
    };
    if (draft.from) q.from = draft.from;
    if (draft.to) q.to = draft.to;
    setParams(q, { replace: true });
  }

  function reset() {
    setDraft({ from: '', to: '', zThreshold: '2' });
    setParams({}, { replace: true });
  }

  const meta = dash.data?.meta;

  return (
    <section className="page--wide join-dash">
      <header className="join-dash__top">
        <div>
          <div className="join-dash__eyebrow">Acquisition analytics</div>
          <h1 className="page-title">신규가입 전환 대시보드 (요일별)</h1>
          <p className="page-desc">
            신규가입 페이지 유니크 유저수와 실제 가입자수를 시간대별로 비교합니다.
          </p>
        </div>
        <div className="join-criteria join-criteria--hourly">
          <span className="join-criteria__dot" />
          <span>
            기준: 동일 <b>요일 × 시간</b> 평균·중앙값·표준편차 비교 (Z ±{meta?.zThreshold ?? zThreshold})
            {meta && (
              <>
                <br />
                고정 기준기간 {meta.baselineStart} ~ {meta.baselineEnd} · 표본{' '}
                {fmtNum(meta.baselineSampleDays)}일 · 기준 전환율 {fmtRate(meta.baselineConversion)}
              </>
            )}
          </span>
        </div>
      </header>

      {/* 감시용 배너. 아래 표·차트는 '왜 그런가' 를 파는 조사용이고, 이건
          '지금 이상한가' 에만 답한다 - 스크롤 없이 보이도록 맨 위에 둔다. */}
      <JoinHealthBanner />

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">조회 조건</h2>
            <p className="panel__note">
              요일별 평균은 조회 기간과 무관하게 <b>{meta?.baselineStart ?? '고정 기준일'} ~ 현재</b>{' '}
              고정 기준으로 계산합니다.
            </p>
          </div>
          {dash.data && (
            <span className="panel__count">
              {dash.data.meta.weekLabel}요일 {fmtNum(dash.data.summary.dayCount)}일 ·{' '}
              {fmtNum(dash.data.summary.hourCount)}시간
            </span>
          )}
        </div>

        <div className="join-weekbar">
          <span className="join-weekbar__label">요일</span>
          {DOW_LABELS.map((label, value) => (
            <button
              key={label}
              type="button"
              className={[
                'join-weekbtn',
                value === week ? 'join-weekbtn--on' : '',
                value === 0 ? 'join-weekbtn--sun' : value === 6 ? 'join-weekbtn--sat' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={value === week}
              onClick={() => apply({ week: value })}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="join-filter">
          <label className="field">
            <span className="field__label">시작일</span>
            <input
              className="input"
              type="date"
              value={draft.from || (meta?.baselineStart ?? '')}
              onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field__label">종료일</span>
            <input
              className="input"
              type="date"
              value={draft.to || (meta?.baselineEnd ?? '')}
              onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field__label">Z 임계값</span>
            <input
              className="input"
              type="number"
              min={0.5}
              max={5}
              step={0.1}
              value={draft.zThreshold}
              onChange={(e) => setDraft((d) => ({ ...d, zThreshold: e.target.value }))}
            />
          </label>
          <div className="join-filter__actions">
            <button type="button" className="btn btn--primary" onClick={() => apply({})}>
              조회
            </button>
            <button type="button" className="btn btn--ghost" onClick={reset}>
              초기화
            </button>
          </div>
        </div>
      </div>

      {dash.isPending ? (
        <Spinner />
      ) : dash.isError ? (
        <ErrorBox error={dash.error} onRetry={() => void dash.refetch()} />
      ) : (
        <WeekdayBody data={dash.data} />
      )}
    </section>
  );
}

function WeekdayBody({ data }: { data: WeekDashboard }) {
  const { summary, meta, focus } = data;
  const [hovered, setHovered] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const [chart, setChart] = useState<ChartMode>('average');
  const [selectedDow, setSelectedDow] = useState(meta.week);
  const [helpOpen, setHelpOpen] = useState(false);

  const activeDate = pinned ?? hovered ?? data.days[0]?.date ?? null;
  const activeDay = data.days.find((d) => d.date === activeDate) ?? null;

  /**
   * 원본 rateClass - 고정 임계값이 아니라 **기준 기간의 실제 전환율** 과 견준다.
   * 70% 미만이면 나쁨, 90% 미만이면 주의. 요일·시간마다 전환율 수준이 다른
   * 화면이라 "30% 이하면 주의" 같은 고정선을 쓸 수가 없다.
   */
  function rateClass(v: number | null): string {
    const base = meta.baselineConversion;
    if (v === null || base === null || !(base > 0)) return '';
    const ratio = v / base;
    if (ratio < 0.7) return 'td-danger';
    if (ratio < 0.9) return 'join-rate--warn';
    return 'td-ok';
  }

  function zClass(v: number | null): string {
    if (v === null) return '';
    if (v <= -meta.zThreshold) return 'td-danger';
    if (v <= -meta.zWarnThreshold) return 'join-rate--warn';
    if (v >= meta.zThreshold) return 'join-z--high';
    return '';
  }

  /** 원본 focusVerdict */
  function verdict(): string {
    if (!focus.available) return '데이터 없음';
    if (focus.sampleCount < 2) return '기준 표본 부족';
    if (focus.z === null) return '편차 없음';
    if (focus.z <= -meta.zThreshold) return '급감 의심';
    if (focus.z <= -meta.zWarnThreshold) return '평균 미달';
    if (focus.z >= meta.zThreshold) return '급증';
    return '정상 범위';
  }

  const chartMax = {
    conversion: Math.max(1, ...data.hourBucket.map((b) => b.conversion ?? 0)),
    inflow: Math.max(1, ...data.hourBucket.map((b) => b.inflowRate ?? 0)),
    average: Math.max(1, ...data.dowHourStats.filter((s) => s.dow === selectedDow).map((s) => s.avgJoinTotal)),
  };
  const barWidth = (value: number | null, field: ChartMode) => {
    const v = Number(value) || 0;
    if (v <= 0) return '0%';
    return `${Math.max(2, Math.round((v / chartMax[field]) * 100))}%`;
  };

  return (
    <>
      <div className="join-metrics">
        <Metric tone="primary" label="가입 페이지 유입" value={fmtNum(summary.joinTotal)}
          sub={`PC ${fmtNum(summary.joinP)} / Mobile ${fmtNum(summary.joinM)}`} />
        <Metric label="실제 가입자" value={fmtNum(summary.memberTotal)}
          sub={`PC ${fmtNum(summary.memberP)} / Mobile ${fmtNum(summary.memberM)}`} />
        <Metric
          tone={rateClass(summary.conversion) === 'td-danger' ? 'danger' : rateClass(summary.conversion) === 'join-rate--warn' ? 'warn' : 'ok'}
          label="전체 전환율"
          value={fmtRate(summary.conversion)}
          sub="가입자 / 가입 페이지 유입"
        />
        <Metric label="소셜 전환 평균" value={fmtRate(summary.socialConversion)}
          sub={`일반 전환 평균 ${fmtRate(summary.normalConversion)}`} />
        <Metric tone="warn" label="주의 시간" value={fmtNum(summary.warnCount)}
          sub={`주의 ${fmtNum(summary.warnCount)} · 기준부족 ${fmtNum(summary.lowSampleCount)}`} />
        <Metric tone="danger" label="문제 의심"
          value={fmtNum(summary.dangerCount + summary.trackingIssueCount)}
          sub={`급감/누락 ${fmtNum(summary.dangerCount + summary.trackingIssueCount)} · 급증 ${fmtNum(summary.spikeCount)}`} />
      </div>

      <div className="panel">
        <div className="panel__head">
          <div>
            <h2 className="panel__title">기준 시점 진단</h2>
            <p className="panel__note">
              {focus.date} ({focus.dowLabel}) {focus.hourLabel} · 동일 요일·시간 표본{' '}
              {fmtNum(focus.sampleCount)}건 · 고정 기준 {meta.baselineStart} ~ {meta.baselineEnd}
            </p>
          </div>
          <span className="panel__count">{verdict()}</span>
        </div>
        {focus.available ? (
          <div className="join-metrics">
            <Metric tone="primary" label="해당 시간 유입" value={fmtNum(focus.joinTotal)}
              sub={`가입 ${fmtNum(focus.memberTotal)} · 전환 ${fmtRate(focus.conversion)}`} />
            <Metric label="요일×시간 평균" value={fmtAvg(focus.avg)} sub={`중앙값 ${fmtAvg(focus.median)}`} />
            <Metric label="표준편차" value={fmtAvg(focus.stddev)} sub={`표본 ${fmtNum(focus.sampleCount)}건`} />
            <Metric
              tone={focus.ratio === null ? undefined : focus.ratio < 70 ? 'danger' : focus.ratio < 90 ? 'warn' : 'ok'}
              label="평균 대비"
              value={fmtRate(focus.ratio)}
              sub={`평균 ${fmtAvg(focus.avg)}명 기준`}
            />
            <Metric
              tone={zClass(focus.z) === 'td-danger' ? 'danger' : zClass(focus.z) === '' ? 'ok' : 'warn'}
              label="Z-Score"
              labelExtra={
                <button type="button" className="join-help" aria-label="Z-Score 설명 열기" onClick={() => setHelpOpen(true)}>
                  ?
                </button>
              }
              value={fmtZ(focus.z)}
              sub={
                focus.z === null
                  ? '표준편차 0 또는 표본 부족'
                  : Math.abs(focus.z) >= meta.zThreshold
                    ? `임계값 ±${meta.zThreshold} 초과`
                    : `임계값 ±${meta.zThreshold} 이내`
              }
            />
            <Metric label="요일×시간 평균 전환율" value={fmtRate(focus.avgConversion)}
              sub={`해당 시간 ${fmtRate(focus.conversion)}`} />
          </div>
        ) : (
          <div className="join-empty">기준 시점의 집계 데이터가 없습니다.</div>
        )}
      </div>

      <div className="join-split">
        <div className="panel">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">{meta.weekLabel}요일 일자별 상세</h2>
              <p className="panel__note">
                일자 행에 마우스를 올리면 그 날의 시간대별 내역이 오른쪽에 펼쳐집니다. 눌러서 고정할 수
                있습니다. 기준값은 같은 요일의 다른 날짜들입니다.
              </p>
            </div>
            <span className="panel__count">{fmtNum(data.days.length)}일</span>
          </div>
          <div className="table-wrap join-scroll">
            <table className="table table--dense">
              <thead>
                <tr>
                  <th>일자</th>
                  <th>상태</th>
                  <th style={{ textAlign: 'right' }}>유입</th>
                  <th style={{ textAlign: 'right' }}>가입</th>
                  <th style={{ textAlign: 'right' }}>전환율</th>
                  <th style={{ textAlign: 'right' }}>Z</th>
                  <th style={{ textAlign: 'right' }}>요일평균</th>
                  <th style={{ textAlign: 'right' }}>중앙값</th>
                  <th>PC</th>
                  <th>Mobile</th>
                  <th>문제시간</th>
                </tr>
              </thead>
              <tbody>
                {data.days.map((d) => (
                  <tr
                    key={d.date}
                    className={[rowClass(d.status), activeDate === d.date ? 'join-row--active' : '']
                      .filter(Boolean)
                      .join(' ')}
                    onMouseEnter={() => setHovered(d.date)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => setPinned((p) => (p === d.date ? null : d.date))}
                  >
                    <td className="td-muted">
                      {d.date} ({d.dowLabel})
                    </td>
                    <td>
                      <span className={`badge ${STATUS_TONE[d.status]}`} title={dayTitle(d)}>
                        {d.statusLabel}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="join-value">
                        <b>{fmtNum(d.joinTotal)}</b>
                        <DeltaPill current={d.joinTotal} average={d.dayAvg} kind="count" tolerance={meta.tolerance} title={dayTitle(d)} />
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <b>{fmtNum(d.memberTotal)}</b>
                    </td>
                    <td style={{ textAlign: 'right' }} className={rateClass(d.conversion)}>
                      <span className="join-value">
                        {fmtRate(d.conversion)}
                        {d.conversion !== null && d.dayAvgConversion !== null && (
                          <DeltaPill
                            current={d.conversion}
                            average={d.dayAvgConversion}
                            kind="rate"
                            tolerance={meta.tolerance}
                            title={`${d.dowLabel}요일 평균 전환율 ${fmtRate(d.dayAvgConversion)}`}
                          />
                        )}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }} className={zClass(d.dayZ)} title={dayTitle(d)}>
                      {fmtZ(d.dayZ)}
                    </td>
                    <td style={{ textAlign: 'right' }} className="td-muted">{fmtAvg(d.dayAvg)}</td>
                    <td style={{ textAlign: 'right' }} className="td-muted">{fmtAvg(d.dayMedian)}</td>
                    <td className="td-muted">
                      {fmtNum(d.joinP)}/{fmtNum(d.memberP)} ({fmtRate(d.pcConversion)})
                    </td>
                    <td className="td-muted">
                      {fmtNum(d.joinM)}/{fmtNum(d.memberM)} ({fmtRate(d.mobileConversion)})
                    </td>
                    <td>
                      {d.dangerCount > 0 && <span className="badge badge--danger">위험 {d.dangerCount}</span>}
                      {d.warnCount > 0 && <span className="badge badge--warn">주의 {d.warnCount}</span>}
                      {d.dangerCount === 0 && d.warnCount === 0 && <span className="td-muted">-</span>}
                    </td>
                  </tr>
                ))}
                {data.days.length === 0 && (
                  <tr>
                    <td colSpan={11} className="join-empty">
                      {meta.weekLabel}요일 데이터가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="join-side">
          <div className="panel">
            <div className="panel__head">
              <div>
                <h2 className="panel__title">시간대별 상세</h2>
                {activeDay ? (
                  <p className="panel__note">
                    {activeDay.date} ({activeDay.dowLabel}) · 유입 {fmtNum(activeDay.joinTotal)} · 가입{' '}
                    {fmtNum(activeDay.memberTotal)} · 전환 {fmtRate(activeDay.conversion)}
                    <br />
                    {activeDay.dowLabel}요일 평균 {fmtAvg(activeDay.dayAvg)}명 (표본{' '}
                    {fmtNum(activeDay.daySampleCount)}일)
                  </p>
                ) : (
                  <p className="panel__note">왼쪽 일자에 마우스를 올리면 그 날의 시간대별 내역이 표시됩니다.</p>
                )}
              </div>
              {activeDay && (
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={pinned === activeDay.date}
                    onChange={() => setPinned((p) => (p === activeDay.date ? null : activeDay.date))}
                  />
                  📌 고정
                </label>
              )}
            </div>
            {activeDay && activeDay.hourly.length > 0 ? (
              <div className="table-wrap join-scroll">
                <table className="table table--dense">
                  <thead>
                    <tr>
                      <th>시간</th>
                      <th>상태</th>
                      <th style={{ textAlign: 'right' }}>유입</th>
                      <th style={{ textAlign: 'right' }}>가입</th>
                      <th style={{ textAlign: 'right' }}>전환율</th>
                      <th style={{ textAlign: 'right' }}>Z</th>
                      <th style={{ textAlign: 'right' }}>평균</th>
                      <th style={{ textAlign: 'right' }}>누적</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeDay.hourly.map((h, i) => (
                      <tr
                        key={h.hour}
                        className={rowClass(h.status)}
                        // 원본 hourHeatStyle - 그 날 안에서 유입이 많은 시간일수록
                        // 배경을 진하게. 어느 시간대에 몰리는지 눈으로 잡으라는 것.
                        style={heatStyle(h.joinTotal, activeDay.hourly)}
                      >
                        <td className="td-muted">{h.hourLabel}</td>
                        <td>
                          <span className={`badge ${STATUS_TONE[h.status]}`} title={h.flags.join(' / ') || h.statusLabel}>
                            {h.statusLabel}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span className="join-value">
                            <b>{fmtNum(h.joinTotal)}</b>
                            <DeltaPill current={h.joinTotal} average={h.baselineAvg} kind="count" tolerance={meta.tolerance} title={baselineTitle(h)} />
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <b>{fmtNum(h.memberTotal)}</b>
                        </td>
                        <td style={{ textAlign: 'right' }} className={rateClass(h.conversion)}>
                          {fmtRate(h.conversion)}
                        </td>
                        <td style={{ textAlign: 'right' }} className={zClass(h.baselineZ)} title={baselineTitle(h)}>
                          {fmtZ(h.baselineZ)}
                        </td>
                        <td style={{ textAlign: 'right' }} className="td-muted" title={baselineTitle(h)}>
                          {fmtAvg(h.baselineAvg)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <b>{fmtNum(activeDay.hourly.slice(0, i + 1).reduce((s, x) => s + x.joinTotal, 0))}</b>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="join-empty">{activeDay ? '시간대별 데이터가 없습니다.' : '표시할 일자가 없습니다.'}</div>
            )}
          </div>

          <div className="panel">
            <div className="panel__head">
              <div>
                <h2 className="panel__title">우선 확인 시간</h2>
                <p className="panel__note">현재값 / 동일 요일·시간 평균</p>
              </div>
              <span className="panel__count">상위 {fmtNum(data.issues.length)}건</span>
            </div>
            <div className="table-wrap join-scroll">
              <table className="table table--dense">
                <thead>
                  <tr>
                    <th>시간</th>
                    <th>상태</th>
                    <th style={{ textAlign: 'right' }}>유입</th>
                    <th style={{ textAlign: 'right' }}>가입</th>
                    <th style={{ textAlign: 'right' }}>전환율</th>
                    <th style={{ textAlign: 'right' }}>Z</th>
                  </tr>
                </thead>
                <tbody>
                  {data.issues.map((r) => (
                    <tr key={`${r.date}_${r.hour}`} className={rowClass(r.status)}>
                      <td className="td-muted">
                        {r.date} ({r.dowLabel}) {r.hourLabel}
                      </td>
                      <td>
                        <span className={`badge ${STATUS_TONE[r.status]}`} title={r.flags.join(' / ') || r.statusLabel}>
                          {r.statusLabel}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="join-value">
                          <b>{fmtNum(r.joinTotal)}</b>
                          <DeltaPill current={r.joinTotal} average={r.baselineAvg} kind="count" tolerance={meta.tolerance} title={baselineTitle(r)} />
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <b>{fmtNum(r.memberTotal)}</b>
                      </td>
                      <td style={{ textAlign: 'right' }} className={rateClass(r.conversion)}>
                        <span className="join-value">
                          {fmtRate(r.conversion)}
                          {r.conversion !== null && r.baselineConversion !== null && (
                            <DeltaPill
                              current={r.conversion}
                              average={r.baselineConversion}
                              kind="rate"
                              tolerance={meta.tolerance}
                              title={`${r.baselineLabel} 평균 전환율 ${fmtRate(r.baselineConversion)}`}
                            />
                          )}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }} className={zClass(r.baselineZ)} title={baselineTitle(r)}>
                        {fmtZ(r.baselineZ)}
                      </td>
                    </tr>
                  ))}
                  {data.issues.length === 0 && (
                    <tr>
                      <td colSpan={6} className="join-empty">
                        문제 의심 시간이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel">
            <div className="panel__head">
              <div>
                <h2 className="panel__title">
                  {chart === 'inflow' ? '시간대 평균 유입률' : chart === 'average' ? '요일 × 시간 평균 유입자' : '시간대 평균 전환율'}
                </h2>
                <p className="panel__note">
                  {chart === 'inflow'
                    ? '조회 기간 전체 유입 대비'
                    : chart === 'average'
                      ? `기준 기간 ${meta.baselineStart} ~ ${meta.baselineEnd}`
                      : '조회 기간 합산 기준'}
                </p>
              </div>
              <div className="tab-group">
                {(
                  [
                    ['conversion', '전환율'],
                    ['inflow', '유입 비중'],
                    ['average', '요일×시간 평균'],
                  ] as [ChartMode, string][]
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={chart === key ? 'tab-btn tab-btn--active' : 'tab-btn'}
                    aria-pressed={chart === key}
                    onClick={() => setChart(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {chart === 'average' && (
              <div className="tab-group join-dowtabs">
                {DOW_LABELS.map((label, value) => (
                  <button
                    key={label}
                    type="button"
                    className={selectedDow === value ? 'tab-btn tab-btn--active' : 'tab-btn'}
                    aria-pressed={selectedDow === value}
                    onClick={() => setSelectedDow(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            <div className="join-bars">
              {Array.from({ length: 24 }, (_, hour) => {
                const bucket = data.hourBucket[hour]!;
                const dowStat = data.dowHourStats.find((s) => s.dow === selectedDow && s.hour === hour);
                const value =
                  chart === 'conversion' ? bucket.conversion : chart === 'inflow' ? bucket.inflowRate : (dowStat?.avgJoinTotal ?? 0);
                return (
                  <div className="join-bar" key={`${chart}_${hour}`}>
                    <span className="join-bar__label">{String(hour).padStart(2, '0')}시</span>
                    <span className="join-bar__track">
                      <span className={`join-bar__fill join-bar__fill--${chart}`} style={{ width: barWidth(value, chart) }} />
                    </span>
                    <span
                      className={chart === 'conversion' ? `join-bar__value ${rateClass(bucket.conversion)}` : 'join-bar__value'}
                      title={
                        chart === 'average' && dowStat
                          ? `평균 ${fmtAvg(dowStat.avgJoinTotal)}명 · 중앙값 ${fmtAvg(dowStat.medianJoinTotal)} · 표준편차 ${fmtAvg(dowStat.stddevJoinTotal)} · 표본 ${fmtNum(dowStat.sampleCount)}건`
                          : undefined
                      }
                    >
                      {chart === 'average' ? `${fmtAvg(value)}명` : fmtRate(value)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {helpOpen && <ZScoreHelp onClose={() => setHelpOpen(false)} />}
    </>
  );
}

const STATUS_TONE: Record<WeekStatus, string> = {
  ok: 'badge--ok',
  warn: 'badge--warn',
  danger: 'badge--danger',
  tracking: 'badge--memo',
  low: 'badge--info',
  spike: 'badge--sky',
  pending: 'badge--grade',
};

function rowClass(status: WeekStatus): string | undefined {
  if (status === 'danger' || status === 'tracking') return 'join-row--danger';
  if (status === 'warn') return 'join-row--warn';
  return undefined;
}

/** 원본 dayTitle - 그 요일의 다른 날짜들과 견준 근거를 툴팁 한 줄로. */
function dayTitle(d: WeekDayRow): string {
  return (
    `${d.dowLabel}요일 평균 ${fmtAvg(d.dayAvg)}명 · 중앙값 ${fmtAvg(d.dayMedian)} · 표준편차 ${fmtAvg(d.dayStddev)} · 표본 ${fmtNum(d.daySampleCount)}일` +
    (d.dayRatio !== null ? ` · 평균 대비 ${fmtRate(d.dayRatio)}` : '') +
    (d.dayZ !== null ? ` · Z ${fmtZ(d.dayZ)}` : '')
  );
}

/** 원본 baselineTitle */
function baselineTitle(h: WeekHourRow): string {
  return (
    `${h.baselineLabel} 평균 ${fmtAvg(h.baselineAvg)}명 · 중앙값 ${fmtAvg(h.baselineMedian)} · 표준편차 ${fmtAvg(h.baselineStddev)} · 표본 ${fmtNum(h.baselineCount)}건` +
    (h.baselineRatio !== null ? ` · 평균 대비 ${fmtRate(h.baselineRatio)}` : '') +
    (h.baselineZ !== null ? ` · Z ${fmtZ(h.baselineZ)}` : '')
  );
}

/** 원본 hourHeatStyle - 그 날 안에서의 상대적 유입량을 배경 진하기로. */
function heatStyle(value: number, hourly: WeekHourRow[]): React.CSSProperties | undefined {
  const values = hourly.map((h) => h.joinTotal);
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (max === min || max === 0) return undefined;
  const ratio = (value - min) / (max - min);
  return { backgroundColor: `rgba(84, 193, 240, ${(0.03 + ratio * 0.16).toFixed(3)})` };
}

function Metric({
  label,
  value,
  sub,
  tone,
  labelExtra,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'primary' | 'ok' | 'warn' | 'danger';
  labelExtra?: React.ReactNode;
}) {
  return (
    <article className={tone ? `join-metric join-metric--${tone}` : 'join-metric'}>
      <span className="join-metric__label">
        {label}
        {labelExtra}
      </span>
      <strong className="join-metric__value">{value}</strong>
      <span className="join-metric__sub">{sub}</span>
    </article>
  );
}

/** 평균 대비 증감 배지. 규칙은 날짜별 화면과 같다 (원본 countDelta/rateDelta). */
function DeltaPill({
  current,
  average,
  kind,
  tolerance,
  title,
}: {
  current: number;
  average: number;
  kind: 'count' | 'rate';
  tolerance: number;
  title: string;
}) {
  const round = (v: number) => Math.round((v + 1e-8) * 10) / 10;
  const diff = round(round(current) - round(average));

  let delta = diff;
  if (kind === 'count') {
    const avg = round(average);
    if (avg !== 0 && (Math.abs(diff) / Math.abs(avg)) * 100 <= tolerance) delta = 0;
  } else if (Math.abs(diff) <= tolerance) {
    delta = 0;
  }
  if (delta === 0) return null;

  const text =
    kind === 'count'
      ? `${fmtAvg(Math.abs(delta))}건 ${delta < 0 ? '감소' : '증가'}`
      : `${fmtAvg(Math.abs(delta))}% ${delta < 0 ? '하락' : '상승'}`;

  return (
    <span className={`join-delta join-delta--${delta < 0 ? 'down' : 'up'}`} title={title}>
      ({text})
    </span>
  );
}

/** 원본의 Z-Score 설명 모달 (물음표 버튼). */
function ZScoreHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Z-Score 설명 닫기">
          ×
        </button>
        <h2 className="modal-title">Z-Score</h2>
        <p className="hint">평균에서 표준편차 몇 배만큼 떨어져 있는지를 나타냅니다.</p>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Z-Score</th>
                <th>의미</th>
                <th>대략적인 위치</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['0', '평균과 동일', '50%'],
                ['1', '평균보다 1σ 높음', '상위 약 16%'],
                ['2', '평균보다 2σ 높음', '상위 약 2.3%'],
                ['3', '평균보다 3σ 높음', '상위 약 0.13%'],
                ['-1', '평균보다 1σ 낮음', '하위 약 16%'],
                ['-2', '평균보다 2σ 낮음', '하위 약 2.3%'],
              ].map(([z, meaning, pos]) => (
                <tr key={z}>
                  <td>{z}</td>
                  <td>{meaning}</td>
                  <td className="td-muted">{pos}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
